import {
    useEffect,
    useState,
} from 'react';
import {
    useInfiniteQuery,
    useQuery,
} from '@tanstack/react-query';

import * as CertificatesService from '../services/CertificatesService';
import {
    Certificate,
    CertificateStatus,
} from '../types';
import {CertificateWriteCapabilities} from '../services/CertificatesService';

// FNP-1193 — the query layer.
//
// ⚠️ THE FILTER IS NOT IN THE QUERY KEY, and that is the opposite of the
// records module — deliberately.
//
// `listCertificates` takes ONLY `limit` and `nextToken`. There is no `filter`
// argument on the schema, so status and target filtering are necessarily
// client-side over the loaded window, exactly like the zones module. Putting a
// client-side filter in the key would refetch identical data under a second
// cache entry for no benefit.
export const certificatesKeys = {
    all: ['certificates'] as const,
    lists: () => [...certificatesKeys.all, 'list'] as const,
    // ⚠️ NO TENANT IN THE KEY, because there is no tenant argument. The server
    // scopes to the caller's own claim; a tenant segment here would be a
    // client-side invention of something the client cannot choose.
    list: () => [...certificatesKeys.lists(), {}] as const,
    detail: (id: string) => [...certificatesKeys.all, 'detail', id] as const,
    capabilities: (connectionId: string) =>
        [...certificatesKeys.all, 'capabilities', connectionId] as const,
    connections: () => [...certificatesKeys.all, 'request-connections'] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown.
export const PAGE_SIZE = 50;

// ⚠️ A SHORT STALE TIME, AND THE REASON IS THE READ-THROUGH DESIGN.
//
// `status` and `expirationDate` are NOT stored server-side — every list call
// describes each certificate at the provider. So a cached page is a snapshot of
// what the provider said at fetch time, and a certificate moving from
// PENDING_VALIDATION to ISSUED is invisible until the cache expires.
//
// 30 seconds, against a 30-minute STUCK threshold: short enough that a status
// transition shows up on the next glance, long enough that scrolling a table
// does not re-describe every certificate. The module also offers an explicit
// reload, because "did it issue yet" is a question an operator asks on their
// own schedule rather than the cache's.
export const STATUS_STALE_TIME = 30_000;

/**
 * FNP-1483 — how often a list or detail holding a settling certificate refetches.
 *
 * Without it the status was read once and a certificate that issued a minute
 * later still read "Pending validation" until someone pressed Reload. Every
 * API read also retries a missing validation-record write, so the refresh is
 * what moves VALIDATION_NOT_WRITTEN on too. The owner chose 30s on 2026-09-24.
 */
export const SETTLING_REFETCH_MS = 30_000;

const SETTLING_STATUSES: ReadonlySet<CertificateStatus> = new Set<CertificateStatus>([
    'PENDING_VALIDATION',
    'VALIDATION_NOT_WRITTEN',
]);

/**
 * The refetch interval for these certificates: `SETTLING_REFETCH_MS` while any
 * of them is still settling, otherwise `false` so a settled list stops polling.
 */
export const settlingRefetchInterval = (
    certificates: readonly Certificate[] | undefined,
): number | false =>
    (certificates ?? []).some((c) => SETTLING_STATUSES.has(c.status))
        ? SETTLING_REFETCH_MS
        : false;

export const useCertificatesInfiniteQuery = () => {
    return useInfiniteQuery({
        queryKey: certificatesKeys.list(),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            CertificatesService.fetchCertificates(PAGE_SIZE, pageParam),
        // ⚠️ THE ONLY END-OF-RESULTS SIGNAL IS A NULL nextToken.
        //
        // MUST return undefined; null or a repeated token makes React Query
        // refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: STATUS_STALE_TIME,
        refetchInterval: (query) => settlingRefetchInterval(
            query.state.data?.pages.flatMap((page) => page?.items ?? []),
        ),
    });
};

// One certificate. Same short stale time and for the same reason — this is the
// view an operator watches while waiting for validation to settle.
export const useCertificateQuery = (id?: string) => {
    return useQuery({
        queryKey: certificatesKeys.detail(id ?? ''),
        queryFn: async () => {
            if (!id) {
                throw new Error('id is required parameter');
            }
            // ⚠️ Returns NULL, never undefined. React Query v5 rejects an
            // undefined resolution outright — it logs "Query data cannot be
            // undefined" and leaves the query neither success nor error.
            return await CertificatesService.fetchCertificate(id) ?? null;
        },
        enabled: Boolean(id),
        staleTime: STATUS_STALE_TIME,
        refetchInterval: (query) => settlingRefetchInterval(
            query.state.data ? [query.state.data] : undefined,
        ),
    });
};

// The capability snapshot behind the write-control hiding decision. Longer
// stale time than the certificate reads: a credential's capabilities change
// when someone re-connects the provider, not while a table is being scrolled.
export const useCertificateCapabilitiesQuery = (connectionId?: string) => {
    return useQuery({
        queryKey: certificatesKeys.capabilities(connectionId ?? ''),
        queryFn: async (): Promise<CertificateWriteCapabilities | null> => {
            if (typeof connectionId === 'undefined' || connectionId === '') {
                throw new Error('connectionId is required parameter');
            }
            return await CertificatesService.fetchConnectionCapabilities(connectionId) ?? null;
        },
        enabled: Boolean(connectionId),
        staleTime: 300_000,
    });
};

// The connections the request form may offer.
export const useCertificateConnectionsQuery = () => {
    return useQuery({
        queryKey: certificatesKeys.connections(),
        queryFn: async (): Promise<CertificatesService.CertificateConnection[]> =>
            await CertificatesService.fetchConnections(),
        staleTime: 300_000,
    });
};

// ─── MODULE-SCOPED FILTER STORE ───
//
// The filter button, the drawer and the table live in DIFFERENT components, so
// a local useState would give each its own copy and the filter would silently
// do nothing. Same idiom as the zones and records modules, so a reader finds
// one pattern rather than three.
//
// ⚠️ BOTH FIELDS ARE CLIENT-SIDE. `listCertificates` has no filter argument at
// all, so these narrow the LOADED WINDOW — which is why the footer reports a
// loaded count rather than a total.
export type CertificatesFilters = {
    status?: string,
    targetType?: string,
}

let globalFilters: CertificatesFilters = {};
const filterListeners: Set<(filters: CertificatesFilters) => void> = new Set();

const notifyFilterChange = (filters: CertificatesFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a filter set in one test leaks
// into the next.
export const resetCertificatesFilters = () => {
    globalFilters = {};
    filterListeners.clear();
};

export const useCertificatesFilter = () => {
    const [filters, setFilters] = useState<CertificatesFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: CertificatesFilters) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        // A consumer mounting after a filter was applied must not start empty.
        setFilters(globalFilters);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    return {
        filters,
        handleSetFilters: (newFilters: CertificatesFilters) => notifyFilterChange(newFilters),
        handleClearFilters: () => notifyFilterChange({}),
        // ⚠️ THE SHORTCUT THAT MATTERS, and it spans TWO statuses.
        //
        // "Show me what needs attention" means STUCK *and*
        // VALIDATION_NOT_WRITTEN — the two states that do not resolve by
        // waiting. A named function rather than every caller assembling the
        // pair by hand, because the pair is the concept and splitting it is how
        // the two states get conflated again.
        handleShowNeedsAttention: () => notifyFilterChange({status: 'NEEDS_ATTENTION'}),
    };
};
